'use client';

import { FC, useState, useCallback, useEffect } from 'react';

// @mui
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Container from '@mui/material/Container';
// routes
// import { PATH_DASHBOARD as paths } from 'src/routes/paths';
// _mock
// import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';

import { useRouter } from 'next/navigation';

import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';

// components
import Iconify from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';

//
import AccountGeneral from '../account-general';
import AccountBilling from '../account-billing';
import { useTranslate } from '../../../../../locales';
import AccountSocialLinks from '../account-social-links';
import { useAuthContext } from '../../../../../auth/hooks';
import AccountNotifications from '../account-notifications';
import AccountChangePassword from '../account-change-password';
import { useResponsive } from '../../../../../hooks/use-responsive';
import { ComponentTypeProps } from '../../../../../types/page-generator';
import {
  _userAbout,
  _userPlans,
  _userPayment,
  _userInvoices,
  _userAddressBook,
} from '../../../../../_mock';
import { ActionTypes, useAppContext } from '../../../../../contexts/AppContext';
import { FamilyCards } from '../../../../../components/family-cards';
import { ICustomerItem } from '../../../../../types/customer-citypeople';
import { getCustomerByPhoneOrMail } from '../../../../../api/customers';
import { TeamCards } from '../../../../../components/team-cards';
import { getMerchant } from '../../../../../api/merchant';
import { IMerchant } from '../../../../../types/merchant';

// ----------------------------------------------------------------------

export type AccountTab = {
  value: string;
  label: string;
  icon: JSX.Element;
};

const TABS: AccountTab[] = [
  {
    value: 'general',
    label: 'General',
    icon: <Iconify icon="solar:user-id-bold" width={24} />,
  },
  {
    value: 'billing',
    label: 'Sales',
    icon: <Iconify icon="solar:bill-list-bold" width={24} />,
  },
  {
    value: 'notifications',
    label: 'Notifications',
    icon: <Iconify icon="solar:bell-bing-bold" width={24} />,
  },
  {
    value: 'social',
    label: 'Adds',
    icon: <Iconify icon="solar:share-bold" width={24} />,
  },
  {
    value: 'security',
    label: 'Security',
    icon: <Iconify icon="ic:round-vpn-key" width={24} />,
  },
  {
    value: 'team',
    label: 'Team',
    icon: <Iconify icon="icon-park-solid:family" width={24} />,
  },
];

// ----------------------------------------------------------------------

const BusinessAccountView: FC<ComponentTypeProps> = ({ block }: ComponentTypeProps) => {
  const { user } = useAuthContext();
  const { dispatch, state } = useAppContext();
  const [customer, setCustomer] = useState<ICustomerItem[] | any>([]);
  const [currentMerchant, setCurrentMerchant] = useState<IMerchant | any>();
  const merchantId = typeof window !== 'undefined' ? localStorage.getItem('merchantId') : '';
  const settings = useSettingsContext();
  const { t } = useTranslate();
  const [currentTab, setCurrentTab] = useState('general');
  const router = useRouter();
  const { logout } = useAuthContext();
  const isMobile = useResponsive('down', 'sm');

  useEffect(() => {
    if (merchantId) {
      getMerchant(merchantId, dispatch).then((response) => {
        setCurrentMerchant(response);
      });
    }
  }, [merchantId]);

  const generalTabItem =
    block && Object.prototype.hasOwnProperty.call(block, 'generalTabItem')
      ? block.generalTabItem
      : true;
  const billingTabItem =
    block && Object.prototype.hasOwnProperty.call(block, 'billingTabItem')
      ? block.billingTabItem
      : true;
  const notificationsTabItem =
    block && Object.prototype.hasOwnProperty.call(block, 'notificationsTabItem')
      ? block.notificationsTabItem
      : true;
  const socialTabItem =
    block && Object.prototype.hasOwnProperty.call(block, 'socialTabItem')
      ? block.socialTabItem
      : true;
  const securityTabItem =
    block && Object.prototype.hasOwnProperty.call(block, 'securityTabItem')
      ? block.securityTabItem
      : true;
  const familyTabItem = Object.prototype.hasOwnProperty.call(block, 'familyTabItem')
    ? block?.familyTabItem
    : true;

  const activeTabNames: string[] = [];

  if (generalTabItem) activeTabNames.push('general');
  if (billingTabItem) activeTabNames.push('billing');
  if (notificationsTabItem) activeTabNames.push('notifications');
  if (socialTabItem) activeTabNames.push('social');
  if (securityTabItem) activeTabNames.push('security');
  if (familyTabItem) activeTabNames.push('team');

  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  const logOut = () => {
    logout();
    router.push('/');
  };

  const setActiveDialog = (payload: null | string) => {
    dispatch({ type: ActionTypes.SET_ACTIVE_DIALOG, payload });
  };

  useEffect(() => {
    if (user) {
      getCustomerByPhoneOrMail(user.phone, user.email, dispatch).then((response: any) => {
        setCustomer(response[0]);
      });
    }
  }, []);

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <Stack direction="row">
        <Tabs
          value={currentTab}
          onChange={handleChangeTab}
          sx={{
            mb: { xs: 3, md: 5 },
            '& .MuiTabs-scrollButtons': {
              width: { xs: '10px' },
              margin: { xs: '5px' },
            },
          }}
        >
          {TABS.filter((tab: AccountTab) => activeTabNames.includes(tab.value)).map(
            (tab: AccountTab) => (
              <Tab
                key={tab.value}
                label={`${t(`${tab.label}`)}`}
                icon={tab.icon}
                value={tab.value}
              />
            )
          )}
        </Tabs>
      </Stack>

      {currentTab === 'general' && generalTabItem && currentMerchant && (
        <AccountGeneral socialLinks={_userAbout.socialLinks} currentMerchant={currentMerchant} />
      )}

      {currentTab === 'billing' && billingTabItem && (
        <AccountBilling
          plans={_userPlans}
          cards={_userPayment}
          // @ts-ignore
          invoices={_userInvoices}
          // @ts-ignore
          addressBook={_userAddressBook}
          block={block}
        />
      )}

      {currentTab === 'notifications' && notificationsTabItem && <AccountNotifications />}

      {currentTab === 'social' && socialTabItem && (
        <AccountSocialLinks socialLinks={_userAbout.socialLinks} />
      )}

      {currentTab === 'security' && securityTabItem && <AccountChangePassword />}

      {currentTab === 'team' && familyTabItem && <TeamCards block={block} customer={customer} />}
    </Container>
  );
};

export default BusinessAccountView;
